/* ============================================================
 * switss-autorail.js — generieke auto-advance voor horizontale
 * carrousels/rails (kaart #186, incl. review-fixronde).
 *
 * Elke rail met [data-sw-autorail="<ms>"] schuift zichzelf om de
 * N milliseconden (attribuutwaarde, default 5000) een kaart
 * verder, met nette pauze-logica:
 *  - geen autoplay als prefers-reduced-motion: reduce (strenger
 *    dan de smooth/auto-switch elders in het theme -- dit beweegt
 *    de pagina zonder gebruikersactie); live gecheckt per tick,
 *    niet eenmalig bij init (change-listener op de MediaQueryList);
 *  - geen autoplay binnen een [data-motion="none"]-sectie;
 *  - alleen doorschuiven als: het tabblad zichtbaar is, de rail
 *    in beeld is (IntersectionObserver), er geen hover/focus
 *    actief is (binnen de pauzezone, zie hieronder), er geen eigen
 *    scroll-animatie nog bezig is, de rail echt overflowt
 *    (scrollWidth > clientWidth) en er >=2 kind-items zijn -- dit
 *    wordt PER TICK opnieuw gecheckt, niet eenmalig bij init (nodig
 *    voor rails die pas na een async fetch gevuld worden, bv.
 *    swm2-related__grid).
 *  - een scroll op de rail die NIET van onze eigen scrollTo/
 *    scrollBy komt (handmatig slepen, de bestaande prev/next-
 *    knoppen, een gefocuste-scrollcontainer-pijltjestoets) betekent
 *    dat de gebruiker de rail zelf heeft bewogen -- autoplay stopt
 *    dan PERMANENT voor die rail (geen cooldown-hervatting: een
 *    kort "even pauzeren en toch weer doorschuiven" voelt raar aan
 *    nadat iemand bewust een andere kaart heeft opgezocht);
 *  - touchstart/wheel/pointerdown zetten wel alleen een tijdelijke
 *    10s-cooldown (aanraken/scrollen zonder dat de rail al bewoog
 *    is nog geen definitieve keuze);
 *  - hover/focus pauzeren zolang ze actief zijn, gebonden op de
 *    optionele pauzezone (data-sw-autorail-pausezone op een
 *    voorouder, bv. om ook de prev/next-knoppen mee te nemen),
 *    anders gewoon op de rail zelf.
 *  - de rail krijgt bij init tabindex="0" als er nog geen tabindex
 *    staat, zodat toetsenbordgebruikers 'm kunnen focussen (focus =
 *    pauze) en pijltjestoetsen op een gefocuste scrollcontainer
 *    'm native kunnen scrollen (telt dan als een echte, permanente
 *    stop via de scroll-listener hierboven) -- WCAG 2.2.2-mitigatie.
 *
 * Patroon: platte IIFE, GEEN custom element (vermijdt de
 * "connectedCallback vuurt voor kinderen bestaan"-valkuil van
 * kaart #146). querySelectorAll('[data-sw-autorail]') + een
 * per-instantie ready-guard (data-sw-autorail-ready), her-init op
 * shopify:section:load. Zelfde stepSize()-aanpak als de bestaande
 * lifestyle-slider (sections/home-lifestyle-slider.liquid). Geladen
 * sitewide vanuit layout/theme.liquid (naast switss-motion.js) --
 * sectie-HTML die de theme-editor via de Section Rendering API
 * injecteert voert <script src>-tags nooit uit, dus per-sectie
 * laden zou autoplay breken zodra een sectie vers wordt toegevoegd
 * in de editor. Dit bestand is zelf een no-op zonder
 * [data-sw-autorail] op de pagina.
 * ============================================================ */
(function () {
  "use strict";

  var DEFAULT_INTERVAL_MS = 5000;
  var INTERACTION_COOLDOWN_MS = 10000;
  /* Debounce voor scroll-settle-detectie: smooth-scroll-events komen per
     animatieframe (~16ms) binnen. 300ms zonder een volgend scroll-event
     betekent dat de geanimeerde scroll voorbij is. Een vast venster (bv.
     1000ms) is te kort voor lange wrap-scrolls (de lifestyle-track is
     2000px+ breed) -- de staart van zo'n animatie zou dan ten onrechte als
     gebruikersinteractie geteld worden, met cascaderende cooldowns tot
     gevolg. */
  var PROGRAMMATIC_SETTLE_MS = 300;

  function initAutorails() {
    var rails = Array.prototype.slice.call(document.querySelectorAll("[data-sw-autorail]"));
    if (!rails.length) return;

    var reduceMotionMql = window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;

    rails.forEach(function (rail) {
      // Defensieve guard (zelfde patroon als initLifestyleSliders /
      // initProductSubnav): voorkomt dubbele timers/listeners bij
      // shopify:section:load (theme-editor).
      if (rail.getAttribute("data-sw-autorail-ready") === "true") return;
      rail.setAttribute("data-sw-autorail-ready", "true");

      // reduced-motion wordt LIVE per tick herlezen (zie advance()) i.p.v.
      // eenmalig hier -- alleen de data-motion="none"-guard is statisch
      // (een sectie schakelt animatieniveau niet live om tijdens het
      // bezoek).
      if (rail.closest('[data-motion="none"]')) return;

      // WCAG 2.2.2-mitigatie: scrollbare regio's horen focusbaar te zijn
      // zodat toetsenbordgebruikers 'm kunnen focussen (focus = pauze) en
      // native pijltjestoetsen 'm kunnen scrollen (telt via de
      // scroll-listener hieronder als een echte, permanente stop). Niet
      // overschrijven als de sectie zelf al een tabindex zet.
      if (!rail.hasAttribute("tabindex")) {
        rail.setAttribute("tabindex", "0");
      }

      var interval = parseInt(rail.getAttribute("data-sw-autorail"), 10) || DEFAULT_INTERVAL_MS;

      // Optionele pauzezone: een voorouder (bv. de wrapper met zowel de
      // track als de prev/next-knoppen) kan hover/focus/interactie breder
      // laten gelden dan alleen de rail zelf.
      var pauseZone = rail.closest("[data-sw-autorail-pausezone]") || rail;

      var paused = false;             // hover of focus actief binnen de pauzezone
      var cooldownUntil = 0;          // timestamp: tijdelijke cooldown na touch/wheel/pointerdown
      var stopped = false;            // permanente stop: de gebruiker heeft de rail zelf bewogen
      var programmaticSettleTimer = null; // debounce-timer: onze eigen scroll-animatie is nog bezig
      var isProgrammaticScrolling = false;

      function stepSize() {
        var firstItem = rail.children[0];
        if (!firstItem) return rail.clientWidth;
        var styles = window.getComputedStyle(rail);
        var gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
        return firstItem.getBoundingClientRect().width + gap;
      }

      function beginProgrammaticScroll() {
        isProgrammaticScrolling = true;
        if (programmaticSettleTimer) window.clearTimeout(programmaticSettleTimer);
        programmaticSettleTimer = window.setTimeout(function () {
          isProgrammaticScrolling = false;
          programmaticSettleTimer = null;
        }, PROGRAMMATIC_SETTLE_MS);
      }

      function extendProgrammaticScroll() {
        // Elk scroll-event tijdens onze eigen animatie verlengt het
        // settle-venster met nog eens PROGRAMMATIC_SETTLE_MS (debounce) --
        // zo dekt dit elke scrollafstand, ongeacht hoe lang de smooth-
        // scroll-animatie duurt.
        if (programmaticSettleTimer) window.clearTimeout(programmaticSettleTimer);
        programmaticSettleTimer = window.setTimeout(function () {
          isProgrammaticScrolling = false;
          programmaticSettleTimer = null;
        }, PROGRAMMATIC_SETTLE_MS);
      }

      function markUserCooldown() {
        cooldownUntil = Date.now() + INTERACTION_COOLDOWN_MS;
      }

      function stopPermanently() {
        if (stopped) return;
        stopped = true;
        if (timerId) window.clearInterval(timerId);
      }

      // ---- Zichtbaarheid: alleen doorschuiven als de rail echt in
      // beeld staat. Zonder IntersectionObserver-support (zeldzaam)
      // valt dit terug op "altijd zichtbaar" -- geen autoplay blokkeren
      // op oude browsers, de overige guards (visibilityState, overflow,
      // cooldown) blijven dan alsnog gelden. ----
      var isVisible = !("IntersectionObserver" in window);
      var io = null;
      if ("IntersectionObserver" in window) {
        io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            isVisible = entry.isIntersecting;
          });
        }, { threshold: 0.35 });
        io.observe(rail);
        rail.__swAutorailIo = io;
      }

      function advance() {
        var reduceMotion = reduceMotionMql ? reduceMotionMql.matches : false;
        if (reduceMotion) return;
        if (stopped) return;
        if (document.visibilityState !== "visible") return;
        if (!isVisible) return;
        if (paused) return;
        if (isProgrammaticScrolling) return; // eigen animatie nog bezig -- geen tweede scroll-doel starten
        if (Date.now() < cooldownUntil) return;
        if (rail.children.length < 2) return;
        if (rail.scrollWidth <= rail.clientWidth + 4) return;

        // Wrap zonder stotterstapje (review-feedback: "gaat niet lekker in
        // elkaar over"): als de resterende afstand tot het einde kleiner is
        // dan een halve stap, is een normale scrollBy een mini-sprongetje
        // dat de browser toch clamt op het echte maximum -- wrap dan
        // meteen naar 0 i.p.v. eerst dat kleine restje te doen. Cadans
        // wordt zo: volle stap, volle stap, ..., wrap.
        var step = stepSize();
        var maxScroll = rail.scrollWidth - rail.clientWidth;
        var remaining = maxScroll - rail.scrollLeft;
        var shouldWrap = remaining <= step / 2;
        beginProgrammaticScroll();
        if (shouldWrap) {
          rail.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          rail.scrollBy({ left: step, behavior: "smooth" });
        }
      }

      // ---- Pauze zolang hover of focus actief is binnen de pauzezone
      // (opgeheven bij leave/blur) ----
      pauseZone.addEventListener("pointerenter", function () { paused = true; });
      pauseZone.addEventListener("pointerleave", function () { paused = false; });
      pauseZone.addEventListener("focusin", function () { paused = true; });
      pauseZone.addEventListener("focusout", function () { paused = false; });

      // ---- Aanraken/scrollen zonder dat de rail al bewoog: tijdelijke
      // cooldown (nog geen definitieve keuze van de gebruiker) ----
      ["touchstart", "wheel", "pointerdown"].forEach(function (eventName) {
        pauseZone.addEventListener(eventName, markUserCooldown, { passive: true });
      });

      // ---- Scroll-events op de rail zelf: ons eigen scroll-settle-venster
      // (fix 1) negeren, elke andere scroll betekent dat de rail zelf
      // bewogen is -- de gebruiker (of de bestaande prev/next-knoppen via
      // hun scrollBy, of een pijltjestoets op de gefocuste rail) heeft de
      // regie gepakt: autoplay stopt dan PERMANENT (fix 2). Verticaal door
      // de pagina vegen raakt scrollLeft van de rail niet en vuurt dus
      // geen scroll-event hier -- mobiele bezoekers die alleen de pagina
      // scrollen houden autoplay gewoon. ----
      rail.addEventListener("scroll", function () {
        if (isProgrammaticScrolling) {
          extendProgrammaticScroll();
          return;
        }
        stopPermanently();
      }, { passive: true });

      var timerId = window.setInterval(advance, interval);
      rail.__swAutorailTimerId = timerId;
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAutorails);
  } else {
    initAutorails();
  }

  // Theme-editor: secties kunnen live opnieuw laden -- de ready-guard
  // hierboven voorkomt dubbele timers voor rails die al draaien.
  document.addEventListener("shopify:section:load", initAutorails);

  // Nette opruiming (niet strikt nodig, de ready-guard dekt het
  // dubbele-timer-risico al af): stop de interval-timer en ontkoppel de
  // IntersectionObserver van rails binnen een sectie die de theme-editor
  // verwijdert.
  document.addEventListener("shopify:section:unload", function (event) {
    if (!event.target || !event.target.querySelectorAll) return;
    var rails = Array.prototype.slice.call(event.target.querySelectorAll("[data-sw-autorail]"));
    rails.forEach(function (rail) {
      if (rail.__swAutorailTimerId) window.clearInterval(rail.__swAutorailTimerId);
      if (rail.__swAutorailIo) rail.__swAutorailIo.disconnect();
    });
  });
})();
